import {
  UserManagerSettings,
  WebStorageStateStore,
  UserManager,
  User,
  OidcClientSettings,
  SignoutResponse
} from 'oidc-client';

import { SVError } from './errors';
import { errorMessage } from './helpers';

export interface IAuthManager {
  signInUser: () => Promise<User>;
  signOut: () => Promise<SignoutResponse>;
  signInCallback: () => Promise<User | undefined>;
  getUserInfo: () => User | undefined;
}

export default class AuthManager implements IAuthManager {
  private manager: UserManager;
  private storage: Storage;
  private settings: Pick<OidcClientSettings, 'scope' | 'authority' | 'client_id'>;
  private sessionKey: string;

  /**
   * Constructor for managing the user credentials
   * @param authority authorization endpoint url (e.g: https://dev.id.core-non-production.servicevic.local:443/openam/oauth2/realms/root/realms/whitelabel)
   * @param client_id type of client to authenticate (e.g: loa2)
   * @param redirect_uri callback redirect uri after processing user request
   * @param storage location of where to store the user crendetials (defaults to "sessionStorage")
   */
  constructor(
    authority: string,
    client_id: string,
    redirect_uri: string,
    storage: Storage = sessionStorage
  ) {
    /**
     * user setttings as part of OIDC
     */
    this.settings = {
      scope: 'openid customer:rw profile email',
      authority,
      client_id // Come from remote call
    };
    this.storage = storage;

    this.sessionKey = `sv-tokenuser:${this.settings.authority}:${this.settings.client_id}`;

    const userManagerSettings: UserManagerSettings = {
      redirect_uri,
      ...this.settings,
      response_type: 'code',
      loadUserInfo: true,
      revokeAccessTokenOnSignout: true,
      extraQueryParams: { createFlag: 'ca' },
      userStore: new WebStorageStateStore({ prefix: 'sv-token', store: storage })
    };

    this.manager = new UserManager(userManagerSettings);
  }

  /**
   * sign in the user to the authorization endpoint. throw an exception on error
   */
  signInUser = async () => {
    await this.manager.signinRedirect();
    return this.manager
      .signinRedirectCallback()
      .then(this.getUserData)
      .catch(err => {
        errorMessage(err);
        throw err;
      });
  };

  /**
   * Signs out and ends the user sesseion
   */
  signOut = async () => {
    await this.manager.signoutRedirect();
    return await this.manager.signoutRedirectCallback();
  };

  /**
   * Extract the user information from oAuth provider, otherwise throw an error if user not exist
   */
  private getUserData = async () => {
    try {
      const userObj = await this.manager.getUser();
      if (!userObj) {
        throw new SVError('No user details found.');
      }
      return userObj;
    } catch (err) {
      errorMessage(err);
      throw err;
    }
  };

  /**
   * process response from the authroization endpoint
   */
  signInCallback = async () => {
    try {
      const user = await this.manager.signinRedirectCallback();
      if (user) {
        return this.getUserData();
      }
    } catch (err) {
      errorMessage(err);
    }
  };

  /**
   * Get JWT Tokens that stored in sessionStorage, otherwise throws an exception
   *  - Access Token: getUserInfoAfterAuth().access_token
   *  - ID Token: getUserInfoAfterAuth().id_token
   */
  getUserInfo: () => User | undefined = () => {
    const tokenDetails = this.storage.getItem(this.sessionKey);
    if (tokenDetails) {
      const jwtToken = JSON.parse(tokenDetails);
      return jwtToken;
    } else {
      return undefined;
    }
  };
}
